// Copyright (c) Docugami, Inc. All rights reserved.

import { useCallback, useEffect, useState } from "react";
import { Engine } from "sdv/engine/Engine";
import { defaultResourceTypes, ResourceTypes } from "sdv/Resources";

export interface ISdvResource<Key extends keyof(ResourceTypes)> {
    value: ResourceTypes[Key];
    set: (value: ResourceTypes[Key]) => void;
}

export function useSdvResource<Key extends keyof(ResourceTypes)>(key: Key, engine: Engine | null): ISdvResource<Key> {
    const [value, setValue] = useState<ResourceTypes[Key]>(defaultResourceTypes[key]);

    useEffect(() => {
        if (!!engine) {
            const onChange = (value: ResourceTypes[Key]) => {
                setValue(value)
            }

            setValue(engine.resourceView.getView(key).value as ResourceTypes[Key]);
            const unregister = engine.resourceView.addListener(key, onChange);
            return () => unregister();
        }
    }, [key, engine]);

    const set = useCallback((newValue: ResourceTypes[Key]) => {
        setValue(newValue)
        engine?.queuePreUpdateCommand((_, resources) => resources.set(key, newValue));
    }, [engine, key]);

    return {
        set: set,
        value: value
    }
}
